<template>
  <q-dialog
    ref="dialogRef"
    v-bind="dialogProps"
    v-model="isVisible"
    :persistent="isPersistent"
    class="base-dialog"
    @hide="onDialogHide"
  >
    <q-card class="full-width">
      <div
        class="bg-white"
        style="position: sticky; top: 0; z-index: 100"
      >
        <q-card-section>
          <div class="row items-center">
            <h2 class="col fs-sm q-ma-none">
              <slot
                name="title-value"
                :title="title"
              >
                {{ title }}
              </slot>
              <slot name="tooltip" />
            </h2>
            <q-btn
              v-if="showCloseIcon"
              v-close-popup
              class="base-dialog-close-icon"
              icon="close"
              flat
              dense
            />
            <div class="row dialog-header-caption">
              <slot
                name="caption-value"
                :title="caption"
              >
                {{ caption }}
              </slot>
            </div>
          </div>
        </q-card-section>
        <q-separator />
        <!-- </div> -->
        <q-card-section>
          <slot>
            <p
              v-if="messageHtml"
              :role="statusProps?.role"
              :aria-live="statusProps?.ariaLive"
              v-html="$sanitize(message)"
            />
            <p
              v-else-if="message"
              :role="statusProps?.role"
              :aria-live="statusProps?.ariaLive"
            >
              {{ message }}
            </p>
          </slot>
        </q-card-section>
        <div
          v-if="actions && actions?.filter(a => a.show !== false).length > 0"
          class="bg-white"
          :style="stickyFooter ? 'position: sticky; bottom: 0; z-index: 100' : undefined"
        >
          <q-separator />
          <q-card-actions
            :vertical="$q.screen.lt.sm"
            :align="$q.screen.lt.sm ? 'center' : 'right'"
            class="q-gutter-y-sm q-pa-md"
          >
            <q-btn
              v-for="action,i in actions"
              v-show="action.show ?? true"
              :key="i"
              :label="action.label"
              :outline="action.outline"
              :color="action.color ?? 'primary'"
              :role="action.loading ? 'status' : 'button'"
              :aria-live="action.loading ? 'polite' : undefined"
              :loading="action.loading"
              :disable="action.disable"
              no-caps
              class="wlk-btn"
              :class="{'full-width': $q.screen.lt.sm}"
              :unelevated="action.btnProps?.unelevated ?? true"
              v-bind="action.btnProps"
              @click="e => action.handler?.(e, { onDialogHide, onDialogOk })"
            >
              <q-tooltip
                v-if="action.tooltip?.show"
                anchor="top middle"
                self="center middle"
              >
                {{ action.tooltip.text }}
              </q-tooltip>
            </q-btn>
          </q-card-actions>
        </div>
      </div></q-card>
  </q-dialog>
</template>
<script setup>
import { ref, watch, computed } from 'vue'
import { useDialogPluginComponent } from 'quasar'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: () => false
  },
  dialogReference: {
    type: Object,
    default: () => null
  },
  dialogProps: {
    type: Object,
    default: () => { }
  },
  title: {
    type: String,
    required: true
  },
  message: {
    type: String,
    default: () => null
  },
  messageHtml: {
    type: Boolean,
    default: () => false
  },
  caption: {
    type: String,
    default: () => ''
  },
  closeIcon: {
    type: Boolean,
    default: () => true
  },
  loading: {
    type: Boolean,
    default: () => false
  },
  closableWhileLoading: {
    type: Boolean,
    default: () => false
  },
  actions: {
    type: Array,
    default: () => null
  },
  stickyFooter: {
    type: Boolean,
    default: () => false
  },
  standalone: {
    type: Boolean,
    default: () => false
  },
  status: {
    type: String,
    default: () => null
  }
})

const emit = defineEmits(['update:modelValue', 'update:dialogReference', ...useDialogPluginComponent.emits])

let dialogRef = null
let onDialogHide = () => {}
let onDialogOk = () => {}

if (props.standalone) {
  const { dialogRef: dr, onDialogHide: odh, onDialogOK: ok } = useDialogPluginComponent()
  dialogRef = dr
  onDialogHide = odh
  onDialogOk = ok
} else if (props.dialogReference) {
  dialogRef = props.dialogReference
} else {
  dialogRef = ref(null)
}

const isVisible = ref(false)
watch(isVisible, v => emit('update:modelValue', v))

const modelValueRef = ref(props.modelValue)

watch(modelValueRef, v => {
  isVisible.value = v
})

watch(dialogRef, v => emit('update:dialogReference', v))

const showCloseIcon = computed(() => props.closeIcon && (!props.loading || props.closableWhileLoading))
const isPersistent = computed(() => !props.closableWhileLoading && props.loading)

const statusProps = computed(() => {
  if (props.status === 'SUCCESS') {
    return {
      role: 'alert'
    }
  }

  if (props.status === 'ERROR') {
    return {
      role: 'alert',
      ariaLive: 'assertive'
    }
  }

  return null
})

</script>
<style lang="scss" scoped>
.base-dialog {
  --wlk-h2-fs-sm: clamp(1rem, 2vw + 1rem, 1.5rem);

  h2.fs-sm {
    font-size: var(--wlk-h2-fs-sm);
    font-family: "Open Sans 700", Arial, sans-serif !important;
  }

  .dialog-header-caption {
    @media screen and (max-width: $breakpoint-xs-max) {
      word-break: break-word;
      hyphens: auto;
    }
  }
}
</style>
